import { productService } from '../services/api';
import { normalizeProductsArray, mergeProductListsById } from './productResponse';
import { productBelongsToCategoryId } from './categoryUtils';
import { filterCatalogProducts, isCatalogProductForDisplay, isDemoProduct } from './isDemoProduct';

const PRODUCTS_PAGE_LIMIT = 100;
const MAX_CATALOG_PAGES = 20;

function normalizeProductsList(productsResponse) {
  if (!productsResponse?.success) return [];
  const raw = productsResponse.products ?? productsResponse.data;
  return normalizeProductsArray(raw);
}

async function fetchAllProductsPaged() {
  const merged = [];
  let page = 1;
  while (page <= MAX_CATALOG_PAGES) {
    const productsResponse = await productService.getProducts({
      page,
      limit: PRODUCTS_PAGE_LIMIT,
      sort: 'createdAt',
      order: 'desc',
    });
    const list = normalizeProductsList(productsResponse);
    merged.push(...list);
    if (list.length < PRODUCTS_PAGE_LIMIT) break;
    page += 1;
  }
  return merged;
}

/** Same strategy as CategoryPage — API category filter + parent fallback + client filter. */
async function fetchProductsForCategoryId(categoryId, parentCategoryId) {
  const cid = String(categoryId || '').trim();
  if (!cid) return [];

  const list = normalizeProductsList(
    await productService.getProducts({
      category: cid,
      limit: PRODUCTS_PAGE_LIMIT,
      sort: 'createdAt',
      order: 'desc',
    })
  );
  if (list.length > 0) {
    return list.filter((p) => productBelongsToCategoryId(p, cid));
  }

  const pid = parentCategoryId != null ? String(parentCategoryId).trim() : '';
  if (pid) {
    const fromParent = normalizeProductsList(
      await productService.getProducts({
        category: pid,
        limit: PRODUCTS_PAGE_LIMIT,
        sort: 'createdAt',
        order: 'desc',
      })
    );
    const filtered = fromParent.filter((p) => productBelongsToCategoryId(p, cid));
    if (filtered.length > 0) return filtered;
  }

  const all = await fetchAllProductsPaged();
  return all.filter((p) => productBelongsToCategoryId(p, cid));
}

function pickCategoryId(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'object') {
    const id = value._id ?? value.id;
    return id != null ? String(id).trim() : '';
  }
  return String(value).trim();
}

function isMongoId(value) {
  return /^[a-f0-9]{24}$/i.test(String(value || '').trim());
}

/** Most specific → broadest category ids on the product. */
export function resolvePdpYmalCategoryLevels(product) {
  if (!product) {
    return { childId: '', subId: '', mainId: '', nameFallback: '' };
  }

  const childId = pickCategoryId(product.childrenCategory);
  let subId = pickCategoryId(product.subcategory);
  let mainId = pickCategoryId(product.category);

  if (!mainId && product.mainCategory) {
    if (typeof product.mainCategory === 'object') {
      mainId = pickCategoryId(product.mainCategory);
    }
  }

  const nameFallback =
    typeof product.category === 'string' && !isMongoId(product.category)
      ? String(product.category).trim()
      : typeof product.mainCategory === 'string'
        ? String(product.mainCategory).trim()
        : '';

  return {
    childId: isMongoId(childId) ? childId : '',
    subId: isMongoId(subId) ? subId : '',
    mainId: isMongoId(mainId) ? mainId : '',
    nameFallback,
  };
}

function productMatchesCategoryLevel(candidate, levels, minLevel) {
  if (minLevel === 'child' && levels.childId) {
    return productBelongsToCategoryId(candidate, levels.childId);
  }
  if (minLevel === 'sub' && levels.subId) {
    return productBelongsToCategoryId(candidate, levels.subId);
  }
  if (minLevel === 'main' && levels.mainId) {
    return productBelongsToCategoryId(candidate, levels.mainId);
  }
  return false;
}

function filterYmalCandidates(pool, currentProduct, levels, minLevel) {
  const currentId = String(currentProduct?.id ?? currentProduct?._id ?? '');
  return filterCatalogProducts(pool).filter((p) => {
    if (isDemoProduct(p)) return false;
    if (!isCatalogProductForDisplay(p)) return false;
    const pid = String(p.id ?? p._id ?? '');
    if (!pid || pid === currentId) return false;
    return productMatchesCategoryLevel(p, levels, minLevel);
  });
}

async function fetchPoolForLevel(levelId, parentId) {
  if (!levelId) return [];
  try {
    return await fetchProductsForCategoryId(levelId, parentId);
  } catch {
    return [];
  }
}

/**
 * Load YMAL pool from the same category / subcategory / children category as the PDP product.
 */
export async function fetchPdpYouMayAlsoLikeProducts(product, { poolMax = 32 } = {}) {
  if (!product) return [];

  const levels = resolvePdpYmalCategoryLevels(product);
  let pool = [];

  if (levels.childId) {
    pool = await fetchPoolForLevel(levels.childId, levels.subId || levels.mainId);
    pool = filterYmalCandidates(pool, product, levels, 'child');
  }

  if (pool.length < 8 && levels.subId) {
    const subPool = await fetchPoolForLevel(levels.subId, levels.mainId);
    pool = mergeProductListsById(pool, filterYmalCandidates(subPool, product, levels, 'sub'));
  }

  if (pool.length < 8 && levels.mainId) {
    const mainPool = await fetchPoolForLevel(levels.mainId, '');
    pool = mergeProductListsById(pool, filterYmalCandidates(mainPool, product, levels, 'main'));
  }

  if (pool.length < 8 && levels.nameFallback) {
    try {
      const byName = normalizeProductsList(
        await productService.getProducts({
          category: levels.nameFallback,
          limit: PRODUCTS_PAGE_LIMIT,
          sort: 'createdAt',
          order: 'desc',
        })
      );
      pool = mergeProductListsById(
        pool,
        filterYmalCandidates(byName, product, levels, 'main')
      );
    } catch {
      /* ignore */
    }
  }

  return pool.slice(0, poolMax);
}
